// composables/useWheelSize.js
export default function useWheelSize() {
  const config = useRuntimeConfig();

  // Compartido por todo el cliente mientras dure la sesión de la SPA
  const csrfToken = useState("wheel_size_csrf_token", () => null);
  const csrfTokenRequest = useState("wheel_size_csrf_token_request", () => null);

  const debug = {
    enabled: config.public.modo === "DEV",
    log: (...args) => debug.enabled && console.log("[useWheelSize]", ...args),
    error: (...args) => debug.enabled && console.error("[useWheelSize]", ...args),
  };

  const obtenerCSRFToken = async (forzar = false) => {
    if (!forzar && csrfToken.value) {
      return csrfToken.value;
    }

    if (!forzar && csrfTokenRequest.value) {
      return await csrfTokenRequest.value;
    }

    csrfTokenRequest.value = $fetch("/api/csrf-token")
      .then((response) => {
        if (!response?.token) {
          throw new Error("No se recibió token CSRF.");
        }

        csrfToken.value = response.token;
        return response.token;
      })
      .finally(() => {
        csrfTokenRequest.value = null;
      });

    return await csrfTokenRequest.value;
  };

  const fetchConCSRF = async (params, reintentar = true) => {
    const token = await obtenerCSRFToken();

    try {
      return await $fetch("/api/wheel-size", {
        params,
        headers: {
          "X-CSRF-Token": token,
        },
      });
    } catch (error) {
      if (error?.statusCode === 403 && reintentar) {
        debug.log("⚠️ Token CSRF inválido o expirado. Renovando token y reintentando...");
        csrfToken.value = null;

        const nuevoToken = await obtenerCSRFToken(true);

        return await $fetch("/api/wheel-size", {
          params,
          headers: {
            "X-CSRF-Token": nuevoToken,
          },
        });
      }

      throw error;
    }
  };

  const handleApiError = (error, context) => {
    debug.error(`${context} error:`, error);

    if (error?.statusCode === 403) {
      debug.error("ERROR Token CSRF inválido");
      csrfToken.value = null;
    } else if (error?.statusCode === 429) {
      debug.error("ERROR Rate Limit: Demasiadas peticiones");
    } else {
      debug.error("ERROR Error:", error);
    }

    return [];
  };

  const getBrands = async () => {
    try {
      debug.log("getBrands() - Iniciando petición");
      const brands = await fetchConCSRF({ action: "brands" });
      debug.log("Marcas recibidas:", brands?.length || 0);
      return brands;
    } catch (error) {
      return handleApiError(error, "getBrands()");
    }
  };

  const getYears = async (brand) => {
    try {
      return await fetchConCSRF({
        action: "years",
        brand,
      });
    } catch (error) {
      return handleApiError(error, "getYears()");
    }
  };

  const getModels = async (brand, year) => {
    try {
      return await fetchConCSRF({
        action: "models",
        brand,
        year,
      });
    } catch (error) {
      return handleApiError(error, "getModels()");
    }
  };

  const getVersions = async (brand, year, model) => {
    try {
      return await fetchConCSRF({
        action: "versions",
        brand,
        year,
        model,
      });
    } catch (error) {
      return handleApiError(error, "getVersions()");
    }
  };

  const getCars = async (brand, year, model, version) => {
    try {
      debug.log("getCars() PARAMETROS:", brand, year, model, version);
      return await fetchConCSRF({
        action: "car",
        brand,
        year,
        model,
        version,
      });
    } catch (error) {
      return handleApiError(error, "getCars()");
    }
  };

  return {
    getBrands,
    getYears,
    getModels,
    getVersions,
    getCars,
  };
}